import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { GiftListItem } from '@nhanho/shared'
import { formatDateVN } from '@nhanho/shared'
import { gifts as api } from '../../lib/api'
import { useList } from '../../lib/useList'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { giftUrl, formatBytes } from './giftShared'

export default function GiftList() {
  // Cố định trước, URL param sau — viết ngược là bộ lọc trong URL bị ghi đè (CLAUDE.md §7).
  const { data, loading, params, set } = useList<GiftListItem>((p) =>
    api.list({ limit: 20, sort: '-updatedAt', ...p }),
  )
  const toast = useToast()
  const [q, setQ] = useState(String(params.search ?? ''))
  const dq = useDebounced(q, 300)
  useEffect(() => { if (dq !== String(params.search ?? '')) set({ search: dq }) }, [dq]) // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(giftUrl(slug))
      toast.success('Đã sao chép link')
    } catch {
      // `navigator.clipboard` cần secure context — http://<ip>:5173 thì không có.
      toast.error('Trình duyệt không cho sao chép ở đây — bấm "Mở" rồi copy từ thanh địa chỉ')
    }
  }

  return (
    <>
      <PageHeader
        title="Quà tặng"
        subtitle={data ? `${data.totalDocs} trang` : undefined}
        actions={<Link to="/qua-tang/new"><Button>+ Trang mới</Button></Link>}
      />
      <div className="mb-4 grid gap-2 md:grid-cols-[1fr_140px]">
        <Input placeholder="Tìm tiêu đề, đường dẫn, ghi chú…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={String(params.published ?? 'all')} onChange={(e) => set({ published: e.target.value === 'all' ? null : e.target.value })}>
          <option value="all">Tất cả</option><option value="true">Đang hiện</option><option value="false">Nháp</option>
        </Select>
      </div>

      {loading && !data ? <Spinner /> : !data?.docs.length ? (
        <EmptyState title="Chưa có trang quà tặng nào" action={<Link to="/qua-tang/new"><Button>+ Trang mới</Button></Link>} />
      ) : (
        <div className="overflow-x-auto rounded-card border border-border bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-sand text-left text-xs uppercase tracking-wider text-ink-muted"><tr>
              <th className="px-3 py-2.5">Trang</th>
              <th className="px-3 py-2.5">Ghi chú</th>
              <th className="px-3 py-2.5">Dung lượng</th>
              <th className="px-3 py-2.5">Sửa lần cuối</th>
              <th className="px-3 py-2.5">Trạng thái</th>
              <th className="px-3 py-2.5 text-right">Link</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {data.docs.map((g) => (
                <tr key={g.id} className="hover:bg-sand/60">
                  <td className="px-3 py-2">
                    <Link to={`/qua-tang/${g.id}`} className="block min-w-0">
                      <div className="truncate font-medium text-ink hover:underline">{g.title}</div>
                      <div className="truncate font-mono text-xs text-ink-muted">/qua-tang/{g.slug}</div>
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-ink-muted">{g.note?.trim() || '—'}</td>
                  <td className="px-3 py-2 text-ink-muted">{formatBytes(g.htmlBytes)}</td>
                  <td className="px-3 py-2 text-ink-muted">{formatDateVN(g.updatedAt) || '—'}</td>
                  <td className="px-3 py-2"><Badge tone={g.published ? 'success' : 'neutral'}>{g.published ? 'Đang hiện' : 'Nháp'}</Badge></td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => void copy(g.slug)}>Sao chép</Button>
                      <a href={giftUrl(g.slug)} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="secondary">Mở</Button></a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-border px-3 py-2.5"><Pagination data={data} onPage={(p) => set({ page: p })} /></div>
        </div>
      )}
    </>
  )
}
